export type MetricDataSource = {
  type: "supabase" | "adapter" | "aggregation" | "derived";
  tables?: string[];
  query: string;
  reportKey?: string;
  dashboardId?: string;
};

export type DashboardMetricId =
  | "finance.giving_period_total"
  | "finance.giving_ytd_total"
  | "finance.contributions_attention"
  | "finance.unallocated_contributions"
  | "finance.needs_review_contributions"
  | "finance.synced_contributions"
  | "finance.pending_sync_contributions"
  | "finance.accounting_sync_status";

export type ReportMetricId =
  | "report.batch_contribution.batch_label"
  | "report.batch_contribution.batch_date"
  | "report.batch_contribution.contribution_count"
  | "report.batch_contribution.total_amount"
  | "report.batch_contribution.cash_count"
  | "report.batch_contribution.check_count"
  | "report.batch_contribution.named_donors"
  | "report.batch_contribution.anonymous_donors"
  | "report.monthly_giving.fund_count"
  | "report.monthly_giving.total_amount"
  | "report.year_end.statements_count"
  | "report.year_end.total_amount"
  | "report.weekly_attendance.event_count"
  | "report.weekly_attendance.total_attendance"
  | "report.checkins.volume"
  | "report.checkins.timing"
  | "report.whos_missing.people_count"
  | "report.new_people.people_count"
  | "report.member_directory.people_count"
  | "report.open_requests.open_count"
  | "report.project_status.project_count"
  | "report.project_status.active_count"
  | "report.giving_gap.lapsed_donor_count"
  | "report.giving_gap.total_gap_amount";

export type MetricId = DashboardMetricId | ReportMetricId;

export type MetricDefinition = {
  id: MetricId;
  label: string;
  description: string;
  dataSource: MetricDataSource;
};

export const metricRegistry: Record<MetricId, MetricDefinition> = {
  "finance.giving_period_total": {
    id: "finance.giving_period_total",
    label: "Giving",
    description: "Total contributions received in the selected period.",
    dataSource: {
      type: "supabase",
      tables: ["contributions"],
      query: "Sum contribution.amount filtered by organization_id and date range for the selected period.",
      dashboardId: "finance",
    },
  },
  "finance.giving_ytd_total": {
    id: "finance.giving_ytd_total",
    label: "Year to Date",
    description: "Total contributions received since the start of the year.",
    dataSource: {
      type: "supabase",
      tables: ["contributions"],
      query: "Sum contribution.amount filtered by organization_id and date >= start of year.",
      dashboardId: "finance",
    },
  },
  "finance.contributions_attention": {
    id: "finance.contributions_attention",
    label: "Contributions",
    description: "Combined count of contributions needing attention.",
    dataSource: {
      type: "derived",
      tables: ["contributions", "allocations"],
      query:
        "Sum unallocated contributions and error/failed contributions from the finance dashboard aggregation.",
      dashboardId: "finance",
    },
  },
  "finance.unallocated_contributions": {
    id: "finance.unallocated_contributions",
    label: "Needs allocation",
    description: "Contributions missing allocations.",
    dataSource: {
      type: "supabase",
      tables: ["contributions", "allocations"],
      query: "Count contributions without allocations in recent contribution list.",
      dashboardId: "finance",
    },
  },
  "finance.needs_review_contributions": {
    id: "finance.needs_review_contributions",
    label: "Needs review",
    description: "Countable contributions with an actionable accounting error.",
    dataSource: {
      type: "supabase",
      tables: ["contributions"],
      query: "Use get_finance_dashboard_contribution_counts for organization-wide lifecycle-aware error counts.",
      dashboardId: "finance",
    },
  },
  "finance.synced_contributions": {
    id: "finance.synced_contributions",
    label: "Synced contributions",
    description: "Countable contributions exported to the connected accounting system.",
    dataSource: {
      type: "supabase",
      tables: ["contributions"],
      query: "Use get_finance_dashboard_contribution_counts and require QBO entity provenance.",
      dashboardId: "finance",
    },
  },
  "finance.pending_sync_contributions": {
    id: "finance.pending_sync_contributions",
    label: "Pending sync",
    description: "Countable contributions waiting for export to the connected accounting system.",
    dataSource: {
      type: "supabase",
      tables: ["contributions"],
      query: "Use get_finance_dashboard_contribution_counts for organization-wide rows without QBO entity provenance.",
      dashboardId: "finance",
    },
  },
  "finance.accounting_sync_status": {
    id: "finance.accounting_sync_status",
    label: "Accounting Sync",
    description: "Overall accounting integration sync health.",
    dataSource: {
      type: "supabase",
      tables: ["finance_integrations"],
      query: "Use finance_integrations.status and last_sync_status to determine sync health.",
      dashboardId: "finance",
    },
  },
  "report.batch_contribution.batch_label": {
    id: "report.batch_contribution.batch_label",
    label: "Batch",
    description: "Batch identifier shown in the report summary.",
    dataSource: {
      type: "adapter",
      tables: ["contributions", "batches"],
      query: "Batch metadata provided by the batch report context.",
      reportKey: "batch-contribution-summary",
    },
  },
  "report.batch_contribution.batch_date": {
    id: "report.batch_contribution.batch_date",
    label: "Date",
    description: "Batch date captured with the batch report context.",
    dataSource: {
      type: "adapter",
      tables: ["contributions", "batches"],
      query: "Batch date provided by the batch report context.",
      reportKey: "batch-contribution-summary",
    },
  },
  "report.batch_contribution.contribution_count": {
    id: "report.batch_contribution.contribution_count",
    label: "Contributions",
    description: "Number of contributions in the batch.",
    dataSource: {
      type: "adapter",
      tables: ["contributions"],
      query: "Count contributions in the selected batch.",
      reportKey: "batch-contribution-summary",
    },
  },
  "report.batch_contribution.total_amount": {
    id: "report.batch_contribution.total_amount",
    label: "Total",
    description: "Total contribution amount in the batch.",
    dataSource: {
      type: "adapter",
      tables: ["contributions", "allocations"],
      query: "Sum contribution.amount for the selected batch.",
      reportKey: "batch-contribution-summary",
    },
  },
  "report.batch_contribution.cash_count": {
    id: "report.batch_contribution.cash_count",
    label: "Cash",
    description: "Count of cash contributions in the batch.",
    dataSource: {
      type: "adapter",
      tables: ["contributions"],
      query: "Count contributions where method is cash in the selected batch.",
      reportKey: "batch-contribution-summary",
    },
  },
  "report.batch_contribution.check_count": {
    id: "report.batch_contribution.check_count",
    label: "Checks",
    description: "Count of check contributions in the batch.",
    dataSource: {
      type: "adapter",
      tables: ["contributions"],
      query: "Count contributions where method is check in the selected batch.",
      reportKey: "batch-contribution-summary",
    },
  },
  "report.batch_contribution.named_donors": {
    id: "report.batch_contribution.named_donors",
    label: "Named Donors",
    description: "Count of contributions with a named donor.",
    dataSource: {
      type: "adapter",
      tables: ["contributions", "people"],
      query: "Count contributions with person_id in the selected batch.",
      reportKey: "batch-contribution-summary",
    },
  },
  "report.batch_contribution.anonymous_donors": {
    id: "report.batch_contribution.anonymous_donors",
    label: "Anonymous/Guest",
    description: "Count of contributions without a named donor.",
    dataSource: {
      type: "adapter",
      tables: ["contributions"],
      query: "Count contributions without person_id in the selected batch.",
      reportKey: "batch-contribution-summary",
    },
  },
  "report.monthly_giving.fund_count": {
    id: "report.monthly_giving.fund_count",
    label: "Funds",
    description: "Number of funds that received contributions this month.",
    dataSource: {
      type: "adapter",
      tables: ["contributions", "allocations", "funds"],
      query: "Count distinct funds from allocations for contributions within the month.",
      reportKey: "monthly-giving-summary",
    },
  },
  "report.monthly_giving.total_amount": {
    id: "report.monthly_giving.total_amount",
    label: "Total",
    description: "Total contributions received this month.",
    dataSource: {
      type: "adapter",
      tables: ["contributions", "allocations"],
      query: "Sum allocation amounts for contributions within the month.",
      reportKey: "monthly-giving-summary",
    },
  },
  "report.year_end.statements_count": {
    id: "report.year_end.statements_count",
    label: "Statements",
    description: "Number of year-end statements generated.",
    dataSource: {
      type: "adapter",
      tables: ["contributions", "people"],
      query: "Count distinct people with contributions in the statement year.",
      reportKey: "year-end-giving-statements",
    },
  },
  "report.year_end.total_amount": {
    id: "report.year_end.total_amount",
    label: "Total",
    description: "Total contribution amount for the statement year.",
    dataSource: {
      type: "adapter",
      tables: ["contributions"],
      query: "Sum contribution.amount for the statement year.",
      reportKey: "year-end-giving-statements",
    },
  },
  "report.weekly_attendance.event_count": {
    id: "report.weekly_attendance.event_count",
    label: "Events",
    description: "Number of events with attendance in the last week.",
    dataSource: {
      type: "adapter",
      tables: ["attendance"],
      query: "Count distinct attendance events from the weekly attendance query.",
      reportKey: "weekly-attendance-report",
    },
  },
  "report.weekly_attendance.total_attendance": {
    id: "report.weekly_attendance.total_attendance",
    label: "Attendance",
    description: "Total attendance count for the last week.",
    dataSource: {
      type: "adapter",
      tables: ["attendance"],
      query: "Sum attendance counts grouped by event from the weekly attendance query.",
      reportKey: "weekly-attendance-report",
    },
  },
  "report.checkins.volume": {
    id: "report.checkins.volume",
    label: "Check-in Volume",
    description: "Daily check-in totals within the selected reporting window.",
    dataSource: {
      type: "adapter",
      tables: ["attendance"],
      query: "Count check-in records grouped by day using attendance.check_in_time or event_date.",
      reportKey: "check-in-volume-timing",
    },
  },
  "report.checkins.timing": {
    id: "report.checkins.timing",
    label: "Check-in Timing",
    description: "Distribution of check-ins by hour to highlight peak arrival windows.",
    dataSource: {
      type: "adapter",
      tables: ["attendance"],
      query: "Bucket check-in records by hour using attendance.check_in_time or event_date.",
      reportKey: "check-in-volume-timing",
    },
  },
  "report.whos_missing.people_count": {
    id: "report.whos_missing.people_count",
    label: "People",
    description: "Number of people missing in the selected window.",
    dataSource: {
      type: "adapter",
      tables: ["people", "attendance"],
      query: "Count missing people provided by the who’s missing query.",
      reportKey: "whos-missing",
    },
  },
  "report.new_people.people_count": {
    id: "report.new_people.people_count",
    label: "New people",
    description: "People created during the current month.",
    dataSource: {
      type: "adapter",
      tables: ["people"],
      query: "Count people created between start of month and report as-of date.",
      reportKey: "new-people-this-month",
    },
  },
  "report.member_directory.people_count": {
    id: "report.member_directory.people_count",
    label: "People",
    description: "People included in the member directory.",
    dataSource: {
      type: "adapter",
      tables: ["people", "households"],
      query: "Count people returned from the member directory adapter.",
      reportKey: "member-directory",
    },
  },
  "report.open_requests.open_count": {
    id: "report.open_requests.open_count",
    label: "Open requests",
    description: "Requests in open, in progress, or waiting status.",
    dataSource: {
      type: "adapter",
      tables: ["requests"],
      query: "Count requests with open, in_progress, or waiting status.",
      reportKey: "open-requests-by-age",
    },
  },
  "report.project_status.project_count": {
    id: "report.project_status.project_count",
    label: "Projects",
    description: "Total projects included in the overview report.",
    dataSource: {
      type: "adapter",
      tables: ["projects"],
      query: "Count projects returned by the project status adapter.",
      reportKey: "project-status-overview",
    },
  },
  "report.project_status.active_count": {
    id: "report.project_status.active_count",
    label: "Active",
    description: "Projects with active status.",
    dataSource: {
      type: "adapter",
      tables: ["projects"],
      query: "Count projects with status active in the adapter rows.",
      reportKey: "project-status-overview",
    },
  },
  "report.giving_gap.lapsed_donor_count": {
    id: "report.giving_gap.lapsed_donor_count",
    label: "Lapsed donors",
    description: "Donors who gave in the prior period but not the current period.",
    dataSource: {
      type: "adapter",
      tables: ["contributions", "people"],
      query: "Compare prior vs current period donor activity and count missing current-period givers.",
      reportKey: "giving-gap-report",
    },
  },
  "report.giving_gap.total_gap_amount": {
    id: "report.giving_gap.total_gap_amount",
    label: "Gap amount",
    description: "Amount given in the prior period by currently lapsed donors.",
    dataSource: {
      type: "adapter",
      tables: ["contributions", "people"],
      query: "Sum prior-period amount for donors with zero current-period amount.",
      reportKey: "giving-gap-report",
    },
  },
};

export const getMetricDefinition = (metricId: MetricId): MetricDefinition => {
  return metricRegistry[metricId];
};

export const buildReportSummaryItem = (metricId: ReportMetricId, value: string | number) => {
  const metric = getMetricDefinition(metricId);
  return { label: metric.label, value };
};
